import type { DisplayMode } from '@treneo/shared/constants';
import { useDateTimeConfig } from '@treneo/shared/contexts';
import { DateTimePickerRange } from '@treneo/shared/forms';
import { useAppTranslations } from '@treneo/shared/i18n';
import { AddIcon, DeleteIcon } from '@treneo/shared/icons';
import type { DateTimeRangeValue } from '@treneo/shared/types';
import { BUTTON_COLORS, BUTTON_VARIANTS, FormHelperText } from '@treneo/shared/ui';
import { getDateLimits } from '@treneo/shared/utils';

import type { CourseDateEntry } from '../../../../data/types';
import type { CourseDateError } from '../../../../utils';
import { convertCourseDateToDateTimeRange } from '../../../../utils/course-dates-utils';

import {
  ActionButtonsContainer,
  CompactButton,
  CourseDateRow,
  DateTimePickerContainer,
  ErrorMessageContainer
} from '../styled';

type Props = {
  courseDate: CourseDateEntry;
  index: number;
  totalDates: number;
  userTimezone: string;
  courseTimezone?: string;
  courseDateErrors: Record<string, CourseDateError>;
  onDateRangeChange: (id: string, dateTimeRange: DateTimeRangeValue) => void;
  onRemoveDate: (id: string) => void;
  onAddDate: () => void;
  onDateRangeBlur?: () => void;
  displayMode?: DisplayMode;
  getCourseDateForDisplay?: (id: string) => CourseDateEntry | undefined;
};

export const CourseDateItem = ({
  courseDate,
  index,
  totalDates,
  userTimezone,
  courseTimezone,
  courseDateErrors,
  onDateRangeChange,
  onRemoveDate,
  onAddDate,
  onDateRangeBlur,
  getCourseDateForDisplay
}: Readonly<Props>) => {
  const { t } = useAppTranslations();
  const { dateFormat, timeFormat } = useDateTimeConfig();
  const { minDate, maxDate } = getDateLimits();

  const handleDateRangeChange = (dateTimeRange: DateTimeRangeValue) => {
    onDateRangeChange(courseDate.id, dateTimeRange);
  };

  const currentCourseDate = getCourseDateForDisplay?.(courseDate.id) ?? courseDate;

  const convertedValue = convertCourseDateToDateTimeRange(
    currentCourseDate,
    courseTimezone ?? userTimezone,
    dateFormat,
    timeFormat
  );

  const dateError = courseDateErrors[courseDate.id];
  const hasDateError = !!dateError;
  let dateErrorMessage = '';
  if (dateError) {
    if (typeof dateError === 'string') {
      dateErrorMessage = t(dateError);
    } else {
      dateErrorMessage = `${t('courseForm.validation.sessionWithNumber', { number: dateError.sessionNumber })} ${t(dateError.messageKey)}`;
    }
  }

  return (
    <CourseDateRow>
      <DateTimePickerContainer>
        <DateTimePickerRange
          value={convertedValue}
          onChange={handleDateRangeChange}
          onBlur={onDateRangeBlur}
          required
          error={hasDateError}
          startMinDate={minDate}
          startMaxDate={maxDate}
          endMinDate={convertedValue.startDate ?? minDate}
          endMaxDate={maxDate}
        />
        {hasDateError && dateErrorMessage && (
          <ErrorMessageContainer>
            <FormHelperText error>{dateErrorMessage}</FormHelperText>
          </ErrorMessageContainer>
        )}
      </DateTimePickerContainer>

      <ActionButtonsContainer>
        <CompactButton
          variant={BUTTON_VARIANTS.outlined}
          startIcon={<DeleteIcon />}
          onClick={() => onRemoveDate(courseDate.id)}
          color={BUTTON_COLORS.error}
          disabled={totalDates <= 1}
          style={{
            opacity: totalDates > 1 ? 1 : 0,
            pointerEvents: totalDates > 1 ? 'auto' : 'none'
          }}
        >
          {t('courseForm.removeDate')}
        </CompactButton>

        <CompactButton
          variant={BUTTON_VARIANTS.outlined}
          startIcon={<AddIcon />}
          onClick={onAddDate}
          style={{ visibility: index === totalDates - 1 ? 'visible' : 'hidden' }}
        >
          {t('courseForm.moreDates')}
        </CompactButton>
      </ActionButtonsContainer>
    </CourseDateRow>
  );
};
